import { Button } from "components/buttons";
import { FormCheckboxList, FormHelperText } from "components/form-fields";
import { ShowHide } from "components/layout";
import { Modal, ModalSize } from "components/notifications";
import { Wizard } from "components/wizard";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import './Hierarchy.css';

export function HierarchyConditionalModal({isOpen, component, isSection, onCancel, onSave, pottentialTriggerQuestions}) {
    const [settings, setSettings] = useState({
        triggerQuestionId: null,
        requiredAnswers: []
    });
    const [isEditing, setIsEditing] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    const [isValid, errors, validate] = useValidation();

    const triggerQuestion = useMemo(() => {
        return settings.triggerQuestionId ? pottentialTriggerQuestions.find(t => t.id === settings.triggerQuestionId) : null;
    }, [settings.triggerQuestionId]);

    const allowedAnswers = useMemo(() => {
        if (triggerQuestion) {
            if (triggerQuestion.$componentType === "YesNoQuestion") {
                return [{
                    id: "True",
                    name: triggerQuestion.labelForYes
                },
                {
                    id: "False",
                    name: triggerQuestion.labelForNo
                }];
            }

            return triggerQuestion.options;
        }
        return [];
    }, [triggerQuestion]);
    
    useEffect(() => {
        if (isOpen && component.condition) {
            setSettings({...component.condition});
        }

        if (isOpen) {
            setCurrentStep(0);
        }

        setIsEditing(!!component.condition);
    }, [isOpen]);

    useEffect(() => {
        validateSettings();
    }, [settings, currentStep]);

    useEffect(() => {
        if (triggerQuestion && settings.requiredAnswers.some(a => !allowedAnswers.map(x => x.id).includes(a))) {
            setSettings({
                ...settings,
                requiredAnswers: []
            });
        }
    }, [allowedAnswers])

    function validateSettings() {
        const rules = [];

        if (currentStep === 0) {
            rules.push({ fieldName: 'triggerQuestionId', required: true});
        } else {
            rules.push({ fieldName: 'requiredAnswers', required: true});
        }

        validate(rules, settings);
    }

    const renderComponentTitle = (useTitle = true) => {
        if (component.title && useTitle) {
            return component.title;
        }

        if (isSection) {
            return `Section ${component.componentNumber}`;
        }

        return `Question ${component.componentNumber}`;
    }

    const setCurrentTriggerQuestion = (newTriggerQuestionId) => setSettings(prev => {
        return {
            triggerQuestionId: newTriggerQuestionId === prev.triggerQuestionId ? null : newTriggerQuestionId,
            requiredAnswers: []
        }
    });

    const setRequiredAnswer = (id) => setSettings(prev => {
        const newAnswers = [...prev.requiredAnswers]

        if (prev.requiredAnswers.includes(id)) {
            newAnswers.splice(newAnswers.indexOf(id), 1);
        } else {
            newAnswers.push(id);
        }

        return {
            ...prev,
            requiredAnswers: newAnswers
        }
    })

    return <Modal
        isOpen={isOpen}
        title={`${isEditing ? "Edit" : "Add"} Condition to ${renderComponentTitle()}`}
        showCloseButton={false}
        clickOutsideClose={false}
        size={ModalSize.MEDIUM}
        >
        <Wizard
            onStepChange={(step) => setCurrentStep(step)}
            onFinish={() => onSave(settings)}
            canProceed={isValid}
            alignButtonsRight={true}
            finishButtonText={`${isEditing ? "Edit" : "Add"} Condition`}
            additionalButtonsStart={<>
                <Button
                    text="Cancel"
                    onClick={onCancel}
                ></Button>
                <ShowHide
                    evaluator={isEditing}
                    show={<Button
                        id="clear-condition"
                        onClick={() => onSave(null)}
                        text={'Clear Condition'}
                        ></Button>}
                />
            </>}
        >
            <div>
                <div className="spacer-bottom-medium">
                    <h3>All previous qualifying questions</h3>
                </div>
                <FormCheckboxList
                    id="checklist-services-to-migrate"
                    items={pottentialTriggerQuestions.map(x => {
                        return {
                            id: x.id, 
                            name: `${x.componentNumber} - ${x.title}`,
                            checked: settings.triggerQuestionId === x.id
                        }
                    })}
                    onChange={(e, id) => setCurrentTriggerQuestion(id)}
                    headingText={""}
                    loading={false}
                    striped={true}
                    searchable={true}
                    fullWidthSearch={true}
                    noDataMessage="No results found, please try a different search term."
                    dangerHelperText={errors.triggerQuestionId}        
                >
                </FormCheckboxList>
            </div>
            <div>
                <div className="spacer-bottom-medium">
                    <h3>Conditions</h3>
                    <FormHelperText>Please select one or more answers to Question {triggerQuestion?.componentNumber} that will trigger {renderComponentTitle(false)} to be shown.</FormHelperText>
                </div>
                <FormCheckboxList
                    id="checklist-services-to-migrate"
                    items={allowedAnswers.map(x => {
                        return {
                            ...x,
                            checked: settings.requiredAnswers.includes(x.id)
                        }
                    })}
                    onChange={(e, id) => setRequiredAnswer(id)}
                    
                    headingText={""}
                    loading={false}
                    striped={true}
                    searchable={false}
                    noDataMessage="No results found, please try a different search term."
                    dangerHelperText={errors.requiredAnswers}        
                >
                </FormCheckboxList>
            </div>
        </Wizard>

    </Modal>
}

HierarchyConditionalModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isSection: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    component: PropTypes.object.isRequired,
    pottentialTriggerQuestions: PropTypes.array.isRequired
}